// Copied from invenire-v2's src/lib/media-import/check-export.ts for the Validate JSON page, which
// runs it in a web worker: keep it free of Angular.
import { validateTracsExport } from './validate-tracs';

/** Largest JSON export an upload may be (the API checks it too). */
export const MAX_IMPORT_FILE_BYTES = 50 * 1024 * 1024; // 50 MB

/** A file's result, in the shape validate-json reports. */
export type ReportedFile = {
  name: string;
  /** The first problems found; `problemCount` counts all of them. */
  problems: string[];
  problemCount: number;
  warnings: string[];
  warningCount: number;
};

export type ExportCheck = ReportedFile & {
  size: number;
  /** Unknown when the file wasn't read: over the upload limit, or a copy of another file. */
  recordCount?: number;
  sessionCount?: number;
};

export const count = (n: number, noun: string) =>
  `${n.toLocaleString('en-US')} ${noun}${n === 1 ? '' : 's'}`;

/** Why the upload refuses a file this size, if it does. */
export function oversizeProblem(bytes: number): string | undefined {
  if (bytes > MAX_IMPORT_FILE_BYTES) {
    return `Is ${(bytes / 1024 / 1024).toFixed(1)} MB; uploads are limited to 50 MB`;
  }
  return undefined;
}

/** Checks an export the way a project upload does. A file over the limit isn't read. */
export async function checkExport(
  name: string,
  size: number,
  read: () => Promise<string>
): Promise<ExportCheck> {
  const tooBig = oversizeProblem(size);
  if (tooBig) {
    return { name, size, problems: [tooBig], problemCount: 1, warnings: [], warningCount: 0 };
  }
  const { problems, problemCount, warnings, warningCount, recordCount, sessionCount } =
    validateTracsExport(await read());
  return { name, size, problems, problemCount, warnings, warningCount, recordCount, sessionCount };
}

/** A file with the same contents as one checked before it: a project accepts each export once. */
export function duplicateCheck(name: string, size: number, sameAs: string): ExportCheck {
  return {
    name,
    size,
    problems: [`Same contents as ${sameAs}`],
    problemCount: 1,
    warnings: [],
    warningCount: 0,
  };
}

const INDENT = '        ';

/** The lines validate-json prints for one file. */
export function reportLines({ name, problems, problemCount, warnings, warningCount }: ReportedFile) {
  const lines = [`${problemCount === 0 ? 'PASS' : 'FAIL'}  ${name}`];
  lines.push(...problems.map((problem) => `${INDENT}${problem}`));
  if (problemCount > problems.length) {
    lines.push(`${INDENT}...and ${count(problemCount - problems.length, 'more problem')}`);
  }
  lines.push(...warnings.map((warning) => `${INDENT}Warning: ${warning}`));
  if (warningCount > warnings.length) {
    lines.push(`${INDENT}...and ${count(warningCount - warnings.length, 'more warning')}`);
  }
  return lines;
}

/** validate-json's closing line, e.g. `3 files checked: 2 passed (1 with warnings), 1 failed`. */
export function reportSummary(files: ReportedFile[]) {
  const failed = files.filter((file) => file.problemCount > 0).length;
  const withWarnings = files.filter((file) => file.problemCount === 0 && file.warningCount > 0).length;
  const warned = withWarnings > 0 ? ` (${withWarnings} with warnings)` : '';
  return `${count(files.length, 'file')} checked: ${files.length - failed} passed${warned}, ${failed} failed`;
}
